import { useMemo } from "react";
import { TriangleAlert } from "lucide-react";
import { inferResultUnit, getBaseUnit, getDisplaySymbol } from "@brewbase/lib";
import type { FormulaContextInput } from "./context";
import { useFormulaContext } from "./hooks/useFormulaContext";

interface FormulaUnitWarningProps {
  formula: string;
  /** The unit the author declared the result is in (`formula_result_unit`). */
  declaredUnit: string | null | undefined;
  context: FormulaContextInput;
}

/**
 * Soft warning shown when the unit a formula structurally produces disagrees
 * with the declared result unit. Inference is best-effort — it only fires when
 * it can confidently name a concrete unit (composite results collapse to
 * dimensionless and never warn), and compares by base unit so a g-vs-kg scale
 * difference (which the author may legitimately have normalised) isn't flagged;
 * only a genuine dimension mismatch is. Renders nothing when there's no
 * declared unit, no confident inference, or they agree.
 */
export function FormulaUnitWarning({
  formula,
  declaredUnit,
  context,
}: FormulaUnitWarningProps) {
  const spec = useFormulaContext(context);

  const inferred = useMemo(
    () => (spec ? inferResultUnit(formula, spec) : undefined),
    [formula, spec],
  );

  if (!declaredUnit || !inferred) return null;
  if (getBaseUnit(inferred) === getBaseUnit(declaredUnit)) return null;

  return (
    <div className="flex items-start gap-1.5 bg-warning/20 px-2 py-1.5">
      <TriangleAlert className="h-3.5 w-3.5 shrink-0 mt-0.5 text-warning-foreground" />
      <p className="text-xs text-warning-foreground">
        This formula evaluates to{" "}
        <span className="font-mono">{getDisplaySymbol(inferred)}</span>, but the
        result unit is set to{" "}
        <span className="font-mono">{getDisplaySymbol(declaredUnit)}</span>.
        Match the result unit to what the formula produces, or convert inside
        the formula.
      </p>
    </div>
  );
}
